import { type ProductModifierGroup, type ProductModifierGroups } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Merges paired "included" and "extras" modifier groups into single groups
 * for UI display.
 *
 * Extras groups are folded into their paired included group. When both an
 * included and extras version of the same ingredient exist, only the included
 * version is kept. Extras-only ingredients (no included counterpart) are
 * preserved in the merged list.
 *
 * Core groups and unpaired groups pass through unchanged.
 */
export function mergePairedModifierGroups(
  groups: ProductModifierGroups,
): MergePairedModifierGroupsResult {
  const extraModifierGroups = groups.filter(checkIfExtraGroup);
  const nonExtraModifierGroups = groups.filter(checkIfNonExtraGroup);

  const extraModifierGroupsByPairedId = new Map(extraModifierGroups.map(toPairedIdEntry));
  const mergeWithPairedExtras = mergeWithPairedExtrasFactory(extraModifierGroupsByPairedId);

  const mergedGroups = nonExtraModifierGroups.map(mergeWithPairedExtras);

  return { mergedGroups };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function mergeWithPairedExtrasFactory(
  extraModifierGroupsByPairedId: Map<string, ProductModifierGroup>,
) {
  return function (group: ProductModifierGroup): ProductModifierGroup {
    if (group.groupRole !== 'included') return group;

    const extrasGroup = extraModifierGroupsByPairedId.get(group.id);

    if (!extrasGroup) return group;

    return mergeIncludedWithExtras(group, extrasGroup);
  };
}

function mergeIncludedWithExtras(
  included: ProductModifierGroup,
  extras: ProductModifierGroup,
): ProductModifierGroup {
  const includedIngredientIds = new Set(
    included.modifications.map((modification) => modification.ingredientId),
  );

  const checkIfUniqueExtra = checkIfUniqueExtraFactory(includedIngredientIds);
  const uniqueExtrasModifications = extras.modifications.filter(checkIfUniqueExtra);

  return {
    ...included,
    maxAggregateQuantity: included.maxAggregateQuantity + extras.maxAggregateQuantity,
    modifications: [...included.modifications, ...uniqueExtrasModifications],
  };
}

function checkIfUniqueExtraFactory(includedIngredientIds: Set<string | null>) {
  return function (modification: ProductModifierGroup['modifications'][number]): boolean {
    return !modification.ingredientId || !includedIngredientIds.has(modification.ingredientId);
  };
}

function toPairedIdEntry(group: ProductModifierGroup): [string, ProductModifierGroup] {
  return [group.pairedGroupId ?? '', group];
}

function checkIfExtraGroup(group: ProductModifierGroup): boolean {
  return group.groupRole === 'extras';
}

function checkIfNonExtraGroup(group: ProductModifierGroup): boolean {
  return group.groupRole !== 'extras';
}

// ─── Types ──────────────────────────────────────────────────────────────────

export type MergePairedModifierGroupsResult = {
  mergedGroups: ProductModifierGroups;
};
